/**
 * The grant page's page-level components (F1).
 *
 * None of these branches on entity type. An edge supplies data; grantOrder
 * decides presence; these render. That is the whole contract — it is what lets
 * one library serve all 22 edges without a mode flag reaching a component.
 */
import React, { useState } from 'react';
import { VerbKey } from './Ask';
// The vocabulary both frontends render from — see rowLabel below for why this
// is imported rather than restated.
import { VERB_LABELS } from '@paradigm/permissions/constants/formA';
import { plural, fmt } from '../../nucleus/format';
import './tokens.css';
import './parts.css';

/** A titled block.
 *
 *  The prototype numbered its sections and the real page lost them, so the
 *  realm list and the boundary arrived as bare lists of controls with nothing
 *  saying what they were or why you would touch them. A heading is the cheapest
 *  thing on the page and the first thing a reader needs.
 */
export function Section({ n, title, hint, aside, tone, actions, children }) {
  // `tone` marks the section you ACT in. Everything else on this page is a
  // statement to read; the realms are the one thing you operate, and until now
  // they were typeset exactly like the parts you cannot change.
  //
  // `actions` sits at the heading's right edge and belongs to the section, not
  // to its title — the Prose | Table switch is a way of reading the section.
  const head = (
    <h2 className="gp-sec__head">
      {n && <span className="gp-sec__n">{n}</span>}
      {title}
      {aside && <span className="gp-sec__aside">{aside}</span>}
    </h2>
  );
  return (
    <section className={`gp-sec${tone ? ` is-${tone}` : ''}`}>
      {actions ? <div className="gp-sec__top">{head}{actions}</div> : head}
      {hint && <p className="gp-sec__hint">{hint}</p>}
      {children}
    </section>
  );
}

/**
 * THE BOND — you, the app, and the exchange between you, above the headline.
 *
 * Both marks are the REGISTER'S OWN: the ofSelf S (`OfselfMark`) and the app's
 * figure (`AppMark`). Nothing here draws a mark of its own; an app the register
 * holds no figure for gets no bond at all, rather than an invented one.
 *
 * The exchange is two hairlines, one each way, each fading in from the side it
 * leaves and ending in a half-barb (⇄). It gains ink as the grant does, and the
 * gap between the marks narrows with it — `state` is none | some | full. It
 * never moves on its own: this is a figure you read, not an animation to watch.
 *
 * The app's figure is CROPPED TO ITS INK after it renders. Register figures sit
 * in a 200-unit box with margins, and the arrow has to stop short of the ink,
 * not of the box — so the box is measured and tightened to what is drawn.
 */
export function Bond({ app, state = 'none', you, names = false }) {
  const appBox = React.useRef(null);
  const [viewBox, setViewBox] = React.useState(undefined);
  React.useLayoutEffect(() => {
    if (viewBox) return;
    const svg = appBox.current?.querySelector('svg');
    if (!svg || typeof svg.getBBox !== 'function') return;
    try {
      const b = svg.getBBox();
      if (b.width && b.height) {
        const pad = 2;
        setViewBox(`${b.x - pad} ${b.y - pad} ${b.width + 2 * pad} ${b.height + 2 * pad}`);
      }
    } catch { /* not laid out (jsdom): the register's own box stands */ }
  }, [viewBox]);
  return (
    <div className={`gp-bond is-${state}${names ? ' is-names' : ''}`} aria-hidden="true" data-testid="bond">
      <span className="gp-bond__you">{you}</span>
      <span className="gp-bond__arrow"><i /><i /></span>
      <span className="gp-bond__app" ref={appBox}>{app(viewBox)}</span>
    </div>
  );
}

/** A show/hide for a list that would otherwise run long.
 *
 *  Shared because three lists needed one and two of them only grew a "show
 *  more" — a page where some sections expand and never close, and others do,
 *  reads as unfinished. Every disclosure on this page is this component.
 */
export function MoreLess({ count, noun = 'more', open, onToggle, openLabel, closeLabel }) {
  if (!count) return null;
  const many = plural(count, noun);
  return (
    <button type="button" className="gp-more" onClick={onToggle}
            aria-expanded={!!open}>
      {open
        ? (closeLabel || `Hide the ${many}`)
        : (openLabel || `Show ${many}`)}
    </button>
  );
}

/**
 * PRICE — WHAT IT COSTS. After C1/C2 names the app, and before the ask.
 *
 * A price above the line that names the app is contextless: "£4/month" means nothing
 * until you know what it is for. But it sits before the permissions, because it gates
 * whether choosing between them matters at all.
 *
 * IT FORMATS NOTHING. `display` arrives from the server as {headline, alternate,
 * detail}, rendered by app/authz/pricing.py — the one renderer in the system. A
 * currency symbol or a toFixed() in this file would be a second one, and then the store
 * badge and this card could disagree about what the same app costs.
 *
 * A FREE APP SAYS SO, QUIETLY. The first version of this rendered nothing at all, on the
 * argument that a badge on the overwhelming majority of apps is noise. That reasoning
 * held only while every app was free. The moment some of them cost money, SILENCE BECOMES
 * AMBIGUOUS: a person cannot tell "this is free" from "the price failed to load", and
 * inference is the wrong mode for anything involving their money. So the component always
 * occupies the same bordered slot, and the free state is a positive statement — lighter
 * in weight than a price, because it asks nothing of the reader.
 *
 * The STORE is different and deliberately still has no free badge: it shows many apps at
 * once, where "Free" on every card genuinely is noise, and a card has no single decision
 * hanging on it. Here there is one app and one decision.
 */
export function PriceLine({ display, alreadyPaid = false,
                           interval, onIntervalChange }) {
  if (!display) return null;

  if (!display.is_paid) {
    return (
      <div className="gp-term gp-price is-free" data-testid="price" role="note">
        <span className="gp-term__k">Cost</span>
        {/* "FREE TO CONNECT", NOT "FREE". One word in a slot read as a value
            someone had pasted there rather than as the page answering its own
            question — and it overstated. ofSelf sets the price of the GRANT and
            knows nothing about what an app charges for inside itself, so "free"
            unqualified is a claim we are not in a position to make, and somebody
            billed by the app later would rightly feel misled by it. "To connect"
            carries that limit in two words; the sentence that used to spell it
            out was the page arguing with a claim nobody had made yet. */}
        <span className="gp-term__v gp-price__head">Free to connect</span>
      </div>
    );
  }

  // An app you have already bought must not read as a fresh charge. The server decides
  // this (`payment_required`), because only it knows whether a live purchase exists.
  if (alreadyPaid) {
    return (
      <div className="gp-term gp-price is-paid-for" data-testid="price" role="note">
        <span className="gp-term__k">Cost</span>
        <span className="gp-term__v">
          <span className="gp-price__head">{display.headline}</span>
          <span className="gp-price__detail">Already active — you will not be charged again.</span>
        </span>
      </div>
    );
  }

  // A FLAG, not a copy match. This sniffed for the literal string 'Price unavailable',
  // so rewording the server's message silently dropped the styling that says an app
  // cannot be authorized.
  const unavailable = display.offerable === false;
  // BOTH intervals must be choosable. The renderer advertises "or £40/year — save 17%"
  // and nothing could take it: no control existed, the checkout was never sent an
  // interval, and the backend defaulted to the first available — so everyone paid
  // monthly while reading about an annual discount.
  const both = Boolean(onIntervalChange && display.intervals?.length > 1);
  return (
    <div className={`gp-term gp-price${unavailable ? ' is-unavailable' : ''}`}
         data-testid="price" role="note">
      <span className="gp-term__k">Cost</span>
      <span className="gp-term__v">
      <span className="gp-price__head">{display.headline}</span>
      {both ? (
        <div className="gp-price__intervals" role="radiogroup" aria-label="Billing period">
          {display.intervals.map(opt => (
            <button
              key={opt.interval}
              type="button"
              role="radio"
              aria-checked={interval === opt.interval}
              className={`gp-price__interval${interval === opt.interval ? ' is-on' : ''}`}
              onClick={() => onIntervalChange(opt.interval)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      ) : (
        display.alternate && <span className="gp-price__alt">{display.alternate}</span>
      )}
      {display.detail && <span className="gp-price__detail">{display.detail}</span>}
      </span>
    </div>
  );
}

/** C1 — who is asking. */
/** WHY YOU ARE HERE — what happened to this grant before, and when.
 *
 * ONE LINE: the rows below already mark anything that changed, so it only has
 * to orient, not explain. Absent on a first grant, so its appearing means
 * something.
 *
 * IT WAS INSIDE C1 UNTIL THE OWNER BECAME CHOOSABLE. It reads like a fact about
 * the app and is not one — `preview.existing` is the prior grant between this
 * app and THIS OWNER, so switching to a group changes which grant it describes,
 * or leaves none. A date attributed to the wrong owner is worse than no date,
 * because it reads as a reassurance. Hence its own slot, after C2.
 */
export function Why({ why }) {
  if (!why) return null;
  return (
    <p className={`gp-why is-${why.tone || 'neutral'}`} data-testid="why" role="note">
      {why.title}
      {why.detail && <> <span>{why.detail}</span></>}
    </p>
  );
}

export function Explanation({ headline, tile, name, sub, count }) {
  return (
    <header className="gp-explain" data-testid="c1">
      <h1 className="gp-explain__h">{headline}</h1>
      {/* NOT EVERY EDGE HAS A FACE. An avatar and a name identify a party you
          are dealing with; on an edge whose other side is your own realm there
          is no party, and the tile rendered "yo · your realms" — initials of a
          pronoun. When there is nothing to identify, this says nothing. */}
      {(name || sub) && (
        <div className="gp-explain__who">
          {tile && <div className="gp-explain__tile">{tile}</div>}
          <div>
            {/* THE HEADCOUNT BELONGS TO THE NAME, NOT THE BLURB. Appended to
                the description behind a "·" it read as the developer's last
                clause — "own every score. · 4 people have this connected".
                It is a fact about the party, so it sits with the party. */}
            {name && (
              <div className="gp-explain__name">
                {name}
                {count && <em className="gp-explain__count">{count}</em>}
              </div>
            )}
            {sub && <div className="gp-explain__sub">{sub}</div>}
          </div>
        </div>
      )}
    </header>
  );
}

/**
 * ADOPTION — the one fact here sourced from OUTSIDE your own account.
 *
 * It sits BELOW the disclaimers, and that ordering is the point: the why-banner
 * and the acting-as line both change what you are being asked, so they have to
 * land before anything else competes for the reading. A statistic between two
 * disclaimers separates them from each other and from the headline they
 * qualify.
 *
 * Honest at the low end — a new app is not a suspicious one, so zero says "you
 * would be the first" rather than rendering as a statistic that reads like a
 * warning the data does not support.
 *
 * NOT "PEOPLE". The count is of accounts that connected this, which is not the
 * same claim as a count of persons, and the page should not make the larger one.
 */
export function Adoption({ children }) {
  if (!children) return null;
  return <p className="gp-adopt" data-testid="adoption">{children}</p>;
}

/** C2 — whose data is at stake, when it isn't yours. One line, load-bearing. */
export function ActingAs({ children }) {
  return <p className="gp-acting" data-testid="c2">{children}</p>;
}

/** C4 — the realm chooser.
 *
 *  Ranked by the SERVER (coverage desc, then fewest entries exposed). The
 *  ranking still decides the ORDER — the row that covers most, exposing least,
 *  sorts first — but it is no longer labelled: the "tightest fit" and "widest"
 *  badges are gone, so the recommendation is now implicit in the order alone.
 *
 *  EVERY REALM YOU OWN IS A ROW, all of them the same kind of row. The list was
 *  filtered to the ones covering part of the request, which made the page assert
 *  "you have four realms" to someone who has six — and the ones it dropped were
 *  the ones you go looking for. Whether a realm covers the ask is answered on the
 *  page already: the verdict under this panel, and the risk block when none of
 *  them cover it. The chooser's job is to list what you have.
 *
 *  Nothing is pre-selected — auto-picking grants from a ceiling nobody chose. */
export function RealmChooser({ realms = [], selectedId, selectedIds, onSelect,
                              noFit, hasAsk = true, onPreview, emptyIsNothing = false }) {
  // A grant is the UNION of the realms you pick — the backend folds them
  // (`fold_union`), and a person's data does not live in exactly one realm. So
  // this is a multi-select, and `selectedId` stays supported for the locked and
  // act forms where exactly one is possible.
  const picked = new Set((selectedIds && selectedIds.length
    ? selectedIds : (selectedId ? [selectedId] : [])).map(String));
  const [atEnd, setAtEnd] = useState(false);

  // NO FILTER. A long list scrolls, five rows at a time, and fades at its lower
  // edge while there is more below; a search box asked a question before the
  // person had seen what there was to search.
  const capped = realms.length > 5;
  const onScroll = (e) => {
    const el = e.currentTarget;
    setAtEnd(el.scrollTop + el.clientHeight >= el.scrollHeight - 2);
  };

  // EVERY ROW SAYS WHAT PRESSING IT DOES. The checkbox alone read as a list of
  // names someone had already ticked; the explicit Add / Added is what makes
  // this the one place on the page you act.
  // THE NULL REALM IS SHARING NOTHING. Where nothing is a complete answer (a group:
  // `emptyIsNothing`), it reads as chosen whenever no other realm is.
  const nothingElse = emptyIsNothing
    && ![...picked].some((id) => !realms.some((x) => x.is_null_realm && String(x.id) === id));
  const row = (r) => {
    const on = picked.has(String(r.id)) || (!!r.is_null_realm && nothingElse);
    return (
      <button
        key={r.id}
        type="button"
        // NO PER-ROW VERDICT. `fits` used to grey the name here, which made a
        // realm that covers nothing look broken rather than simply unhelpful for
        // this ask — and since `required_keys` is empty, `fits` is just "grants
        // anything", the same question the filter above asked. Whether the realms
        // you picked are enough is one sentence under the panel, about the union,
        // which is the level the question is actually answered at.
        className={`gp-realm${on ? ' is-sel' : ''}`}
        aria-pressed={on}
        onClick={() => onSelect?.(r.id)}
        // Considering a realm previews it on the ask above: the lines it would
        // satisfy light up before the click, from the realm's own `missing`.
        onMouseEnter={() => onPreview?.(r)}
        onMouseLeave={() => onPreview?.(null)}
        onFocus={() => onPreview?.(r)}
        onBlur={() => onPreview?.(null)}
      >
        <span className="gp-realm__box" aria-hidden="true" />
        <span className="gp-realm__body">
          <span className="gp-realm__name">{r.name}</span>
        </span>
        <span className="gp-realm__cta" aria-hidden="true">
          {r.is_null_realm ? (on ? 'Shares nothing' : 'Share nothing') : (on ? 'Added' : 'Add')}
        </span>
      </button>
    );
  };

  return (
    <div className="gp-realms" data-testid="c4">
      {realms.length > 0 ? (
        <div className={`gp-realms__scroll${capped ? ' is-capped' : ''}${atEnd ? ' at-end' : ''}`}
             onScroll={capped ? onScroll : undefined}>
          {realms.map(row)}
        </div>
      ) : (
        /* THE ONLY EMPTY STATE LEFT. "None of your realms cover what this needs"
           used to live here too, standing in for the list — but the list is now
           always the list, and that sentence is C9's job (`no_fit`), said once. */
        <p className="gp-nofit" data-testid="no-fit">
          <b>You have no realms yet.</b> Make one, then come back.
        </p>
      )}
    </div>
  );
}

/** ROLE — what a group lets you do there, read only.
 *
 *  A membership is two contracts. The page chooses one of them (what you share,
 *  C4); this states the other — the role you hold and the realm the group puts
 *  you on — so a person joining knows both sides before they press Join. */
export function GroupStanding({ lines = [], note = null }) {
  return (
    <div className="gp-standing" data-testid="group-standing">
      <ul className="gp-standing__lines">
        {lines.map((line) => <li key={line}>{line}</li>)}
      </ul>
      {note && <p className="gp-standing__note">{note}</p>}
    </div>
  );
}

/** C4L — the realm, locked.
 *
 *  A real component, not a fallback: three edges lock the realm for three
 *  different reasons — node-share DERIVES it, sub-entity INHERITS it, plugin-run
 *  BORROWS it. Same shape, different sentence. Offering a picker would be a lie. */
export function RealmLocked({ title, reason }) {
  /* THE SAME ROW THE CHOOSER USES, already chosen. It was its own object — a
   * tinted block with a ◈ — which made "the realm this lands in" look like a
   * different kind of thing from "the realm you pick", on two pages a person
   * meets minutes apart. It is the same thing; the only difference is that here
   * it is settled.
   *
   * A DISABLED BUTTON, not a div dressed as one: the tick is filled, the row
   * cannot be pressed, and a screen reader is told both. Saying so in the aside
   * ("not yours to pick") was the page apologising for a decision it had made
   * for good reasons — the reason line under the name explains it instead. */
  return (
    <button type="button" className="gp-realm is-sel is-locked" disabled
            aria-pressed="true" data-testid="c4l">
      <span className="gp-realm__box" aria-hidden="true" />
      <span className="gp-realm__body">
        <span className="gp-realm__name">{title}</span>
        {reason && <span className="gp-realm__meta">{reason}</span>}
      </span>
    </button>
  );
}

/** C5P — no DLR, so you pick from your own inventory instead of declining theirs. */
export function Pick({ inventory = [], selected = [], onToggle }) {
  const on = new Set(selected.map(String));
  return (
    <div className="gp-pick" data-testid="c5p">
      {inventory.map((row) => (
        <button
          key={row.schema_id}
          type="button"
          className={`gp-pick__row${on.has(String(row.schema_id)) ? ' is-on' : ''}`}
          aria-pressed={on.has(String(row.schema_id))}
          onClick={() => onToggle?.(row.schema_id)}
        >
          <span className="gp-pick__tick" aria-hidden="true" />
          <span className="gp-pick__name">{row.schema_name}</span>
          <span className="gp-pick__n">{fmt(row.count)}</span>
        </button>
      ))}
    </div>
  );
}

/** C6 — the single object in play. Named, never measured. */
export function Item({ title, snippet, meta, schema, diff, verbs }) {
  if (diff) {
    return (
      <div className="gp-diff" data-testid="c6">
        {diff.map((d) => (
          <div className="gp-diff__row" key={d.label}>
            <span className="gp-diff__k">{d.label}</span>
            <span className={d.removed ? 'gp-diff__old' : ''}>{d.value}</span>
          </div>
        ))}
      </div>
    );
  }
  /* A ROW, AND UNMISTAKABLY A NODE. It was a bordered card, a shape that exists
   * nowhere else on the page now that the frames are gone. As a row it joins the
   * page's one grammar — but a row in the ask list means a SCHEMA, a type with
   * verbs an app may use on it, and this is one instance of one.
   *
   * So the two are told apart the way the page already tells identifiers from
   * prose: the title is SANS, because a node's title is something a person
   * wrote, and every schema name on this page is mono. Under it, in the mono
   * qualifier register, what it actually is — `goal · one node`. A schema row
   * never carries a sans title, so the two cannot be read as the same thing. */
  /* THE VERBS SIT WHERE EVERY OTHER ROW'S VERBS SIT, in the same five fixed
   * slots — so the shape of this row is the shape of a row on the app page, and
   * a reader who has learnt one has learnt the other. Today a share grants
   * `read` and the other four are struck; the day it grants more, the row says
   * so without a new layout being invented for it. */
  return (
    <div className="gp-node" data-testid="c6">
      <div className="gp-node__row">
        <span className="gp-node__t">{title}</span>
        <span className="gp-node__key">
          <VerbKey grants={(verbs || ['read']).map((v) => ({ resource: 'nodes', verb: v }))} />
        </span>
      </div>
      {snippet && <div className="gp-node__s">{snippet}</div>}
      {/* THE CAVEAT'S VOICE, because it is doing the caveat's job: a line under a
          row qualifying what the row means. `goal` keeps the mono every schema
          name on this page wears, and the sentence calls it a SCHEMA — the word
          the rest of the product uses for it — so the line says plainly that
          this is one instance of one, which is the distinction a row on the app
          page cannot make. */}
      {schema
        ? <div className="gp-node__m">
            Single node of schema <em className="gp-node__type">{schema}</em>
          </div>
        : meta && <div className="gp-node__m">{meta}</div>}
    </div>
  );
}

/** C7 — everything you have, with only the granted part lit.
 *
 *  Three states, which is `consent ⊆ ceiling ⊆ everything` made visible without
 *  the page ever saying "ceiling". Lit rows carry the verbs held on them, which
 *  is the schema axis of the same grants shown verb-first in C3/C5. */
/** C7 — everything you have, with only the granted part lit.
 *
 *  NO BARS. A bar length is the count relative to the largest type, which is a
 *  fact about your biggest schema rather than about this grant — it looked like
 *  a measure of exposure and measured nothing. The number is the number, and
 *  the VERBS are what is actually worth reading on a lit row. */
export function Boundary({ inventory = [], verbsByType = {}, reachable, inAsk,
                          capabilities, wasAsked = true, empty, notes = {} }) {
  const [showRest, setShowRest] = useState(false);
  const reach = reachable ? new Set(reachable.map(String)) : null;
  const asked = inAsk ? new Set(inAsk.map(String)) : null;

  // THE ASK FIRST, THE REST FOLDED. This listed every schema you own, which at
  // thirty types buried the five the app actually wants under twenty-five it
  // never mentioned. The rest is not deleted — the untouched count is the
  // reassurance — it is just not the thing you are being asked to read.
  const rows = inventory.map((r) => {
    const verbs = verbsByType[r.schema_name] || verbsByType[String(r.schema_id)];
    const state = verbs?.length ? 'lit'
      : (!reach || reach.has(String(r.schema_id)) ? 'reach' : '');
    return { ...r, verbs, state, inAsk: !asked || asked.has(String(r.schema_id)) };
  });
  const primary = rows.filter((r) => r.inAsk);
  const rest = rows.filter((r) => !r.inAsk);

  /* WHAT OF IT IS READ, under a lit row, in the words the realm and contract
     panels use ("Only title", "Only where type is run"): a realm that narrows a
     schema hands it over cut down, and the row has to say so. */
  const line = (r) => (
    <React.Fragment key={r.schema_id}>
      <div className={`gp-brow is-${r.state || 'out'}`}>
        <span className="gp-brow__lab">{r.schema_name}</span>
        <span className="gp-brow__verbs">
          {[...new Set(r.verbs || [])].map((v) => (
            <em key={v} className={`gp-vtag is-${v}`}>{v}</em>
          ))}
        </span>
        <span className="gp-brow__n">{fmt(r.count)}</span>
      </div>
      {r.state === 'lit' && (notes[r.schema_name] || []).map((t) => (
        <div className="gp-brow__note" key={t}>{t}</div>
      ))}
    </React.Fragment>
  );

  return (
    <div className="gp-bound" data-testid="c7">
      {/* NOTHING CHOSEN IS NOT EVERYTHING. With no realm picked this listed all
          43 types you own, which reads as "you are sharing all of this" — the
          exact opposite of the truth, on a page whose whole job is to say what
          leaves. An empty grant has to look empty. */}
      {primary.length === 0 && (
        <p className="gp-bound__none">{empty || 'Nothing yet.'}</p>
      )}
      {primary.map(line)}
      {/* A realm grants more than schemas. One permitting `plugins:execute` was
          granting it silently, because this only ever spoke about types. */}
      {capabilities?.length > 0 && (
        <div className="gp-bound__caps">
          <span className="gp-bound__capslab">and</span>
          {capabilities.map((v) => <em key={v} className="gp-vtag">{v.replace(':', ' ')}</em>)}
        </div>
      )}
      {showRest && rest.length > 0 && (
        <>
          {/* Why the rest is safe differs by edge. An app was handed a list and
              did not name these. A share had no list — these are simply outside
              the realms. Saying "not asked for" on a share invents a request. */}
          <p className="gp-bound__restnote">
            {wasAsked
              ? <>Everything else you have. <b>None of it was asked for.</b></>
              : <>Everything else you have. <b>These realms do not reach it.</b></>}
          </p>
          {rest.map(line)}
        </>
      )}
      <MoreLess count={rest.length} noun="type" open={showRest}
                onToggle={() => setShowRest((v) => !v)}
                openLabel={`Show ${rest.length} more `
                  + `${rest.length === 1 ? 'type' : 'types'} you have, `
                  + (wasAsked ? 'none of them asked for' : 'outside these realms')}
                closeLabel={`Hide the ${rest.length} untouched `
                  + `${rest.length === 1 ? 'type' : 'types'}`} />
    </div>
  );
}


/** ONE VERDICT, ON THE UNION.
 *
 *  The strikethrough on each verb answers "is this reachable". Nothing answered
 *  "am I done" — the reader had to scan every row and total it themselves, on
 *  the one question the disabled button depends on.
 *
 *  The union rule sits WITH the verdict because it EXPLAINS it: two realms
 *  covering everything is the union covering everything. Stated above the list
 *  it was a grey band asserting a rule before there was a result to apply it to.
 */
export function Verdict({ met = 0, total = 0, name }) {
  if (!total) return null;
  const all = met === total;
  return (
    <div className="gp-verdict" data-testid="verdict">
      {/* THE SAME MARK AS A ROW. It was a stroked SVG at full ink — heavier
          than the × on the rows it summarises, and heavy enough to read as a
          close button you could press. Same glyph, same weight, same ink: this
          is a verdict, not a control, and it should look like the marks it is
          totalling. */}
      <span className={`gp-verdict__mark${all ? ' is-met' : ''}`} role="img"
            aria-label={all ? 'Requirements met' : 'Requirements not met'}>
        {all ? '✓' : '×'}
      </span>
      <span>
        {all
          ? <>Covers everything <b>{name}</b> requires.</>
          : met === 0
            ? <>Reaches none of what <b>{name}</b> requires. Add a realm that does.</>
            : <>Covers <b>{met} of {total}</b>. The lines marked × are out of reach.</>}

      </span>
    </div>
  );
}

/** WHAT, EXACTLY, IS OUT OF REACH — and which realm is in the way.
 *
 *  The verdict totals the shortfall ("covers 12 of 15") and each row carries an
 *  ×. Neither says WHY, and the why is the only part a person can act on: a verb
 *  their realms withhold and a type their realms do not contain are fixed by
 *  different realms. The page already knew the difference — `uncovered` and
 *  `reachable` are separate tests in `useGrant` — and showed one word for both.
 *
 *  GROUPED BY CAUSE, NOT LISTED PER ROW. Five rows failing because the realm has
 *  no `delete` is one fact about the realm, and five lines saying so is the same
 *  sentence five times. The rows are named inside their cause so nothing is lost.
 *
 *  It sits under the verdict it explains, and only once a realm is in — before
 *  that there is no shortfall, only an unanswered question.
 */
/* PHRASED AS "<cause> by <realm>", not "<realm> does not allow". The name is
   one realm when you added one and "the realms you added" when you added
   several, and no verb agrees with both — "The realms you added does not allow"
   is what the first phrasing produced. Leading with the cause also puts the
   actionable word first on every line, so the list scans down its own left edge. */
/* NAME THE AXIS, NOT JUST THE VERDICT. "Not allowed by Reading room" and "Not
   included in Reading room" are two sentences a reader has to compare word by
   word to see that they mean different things — and the thing they differ about
   (a permission the realm lacks vs a type it does not hold) is the whole reason
   the split exists. Leading with the noun says it outright, and the two lines
   stay parallel so the one word that changes is the one carrying the meaning. */
/* PASSIVE, SO THE NAME CAN BE EITHER. `n` is one realm's name when you added
   one and "the realms you added" when you added several, and no active verb
   agrees with both — "the realms you added does not grant" is what the first
   attempt produced, for the second time on this page. */
const SHORTFALL_CAUSE = {
  verb: (n) => `Permissions not granted by ${n}`,
  schema: (n) => `Types not held by ${n}`,
  other: (n) => `Narrowed by ${n}`,
  mixed: () => 'Not covered by any one realm you added',
};
/** The same rows once the question below them is answered.
 *
 *  THEY ARE RESTATED, NOT STRUCK THROUGH. Every phrase above is a NEGATIVE — "not
 *  granted by", "not held by" — so a line through one made a double negative: it
 *  is not the case that they are not granted. A ✓ beside it said the same thing a
 *  third way, and the verdict had just changed on the line above, so three things
 *  moved at once to convey one fact.
 *
 *  Positive, parallel, and in the same place: the rows do not move, the reader
 *  compares two states of one sentence, and the tick that caused it is directly
 *  below.
 */
/** How a blocked row names itself IN PROSE: the verb it wanted, on the type it
 *  wanted — and for a capability, the sentence the rest of the page uses for it.
 *
 *  `automations: execute, sub entity: declare` is the wire talking. The prose
 *  view exists so a person does not have to read the wire, and every other line
 *  in it is a sentence; a raw resource:verb pair among them is the one place it
 *  stopped being written for people.
 *
 *  VERB_LABELS, not a map of our own. It is the vocabulary both frontends
 *  already render from (`shared/permissions/constants/formA.js`), so a verb
 *  renamed there is renamed here. A third copy is how the consent screen and
 *  the realm editor come to call one permission two things. The technical form
 *  survives as the fallback, because a verb missing from that table must still
 *  appear rather than vanish from a list of what you are not getting. */
const rowLabel = (g) => {
  if (g.resource === 'nodes') {
    return `${g.verb} on ${g.all_schemas ? 'everything' : (g.schema_name || 'an unnamed type')}`;
  }
  const label = VERB_LABELS[`${g.resource}.${g.verb}`];
  // lower-cased because these land mid-sentence, after "not granted by X:"
  return label ? label[0].toLowerCase() + label.slice(1)
    : `${g.resource.replace(/_/g, ' ')}: ${g.verb}`;
};

/** The rows of one cause, said once per verb instead of once per pair.
 *
 *  A WIDE ASK REPEATED THE VERB UNTIL THE LINE WAS UNREADABLE. Thirteen types
 *  under `create` printed "create on action, create on allocation, create on
 *  availability, …" — thirty-odd phrases where the only thing changing was the
 *  last word, and the verb that was actually withheld was buried in the noise.
 *  The prose above already groups the other way round ("Read, create and edit
 *  your actions, beliefs and goals"), so the shortfall reads as the list it is:
 *  the verb once, then everything it was refused on.
 *
 *  Capabilities keep their own sentences — they have no type to group by — and
 *  follow the verbs, because "view activity log" among `create on …` phrases
 *  reads as one more type otherwise.
 *
 *  Groups are divided by SEMICOLONS, since the names inside them are divided by
 *  commas; with commas doing both jobs "create on action, read on belief" and
 *  "create on action, belief" cannot be told apart. */
const andAll = (xs = []) => (xs.length < 2 ? xs.join('')
  : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

const groupedLabels = (grants = []) => {
  const byVerb = new Map();
  const caps = [];
  for (const g of grants) {
    if (g.resource !== 'nodes') { caps.push(rowLabel(g)); continue; }
    const t = g.all_schemas ? 'everything' : (g.schema_name || 'an unnamed type');
    if (!byVerb.has(g.verb)) byVerb.set(g.verb, []);
    if (!byVerb.get(g.verb).includes(t)) byVerb.get(g.verb).push(t);
  }
  const groups = [...byVerb.entries()].map(([verb, types]) => `${verb} on ${andAll(types)}`);
  const all = [...groups, ...caps];
  // A SEMICOLON ONLY WHEN A COMMA WOULD BE AMBIGUOUS — that is, when some group
  // names more than one type. "delete on notes, edit on notes" needs no heavier
  // punctuation than the sentence it sits in, and getting it reads as a stammer.
  const wide = [...byVerb.values()].some((types) => types.length > 1);
  return all.join(wide ? '; ' : ', ');
};

/* THE SAME FACT IN THE TABLE'S OWN GRAMMAR.
 *
 * Sentences are right under the prose, which is sentences. Under the TABLE they
 * read as a different instrument playing the same note: the rows above are
 * `name · verbs`, in mono, aligned down the page, and three lines of prose
 * beneath them restate that in a voice the section does not use.
 *
 * So the table's shortfall is a continuation of the table — one line per type,
 * the verbs that fell out, then the cause — and only the CAUSE column is new
 * information. Grouped by TYPE here, not by cause, because that is the axis the
 * rows above are already sorted on and the eye is already tracking.
 */
/* The same naming, compressed to the table's register. Each one points at the
   column beside it — "permission" at the verbs, "type" at the name — so the
   row reads as which of its own two halves the realm could not answer. */
const SHORT_TABLE_CAUSE = {
  verb: (n) => `permission not in ${n}`,
  schema: (n) => `type not in ${n}`,
  other: (n) => `narrowed by ${n}`,
  mixed: () => 'not covered by one realm',
};
/** The type a blocked row is about, as the table above names it. */
const typeOf = (g) => (g.resource === 'nodes'
  ? (g.all_schemas ? 'everything' : (g.schema_name || 'unnamed'))
  : g.resource.replace(/_/g, ' '));

/** `resolved` — the same rows, after the question below them has been answered.
 *
 *  THEY STAY PUT. Removing them made the page rearrange itself the moment the box
 *  was ticked: the verdict changed, two rows vanished, everything below jumped up,
 *  and nothing connected any of it to the tick that caused it. Struck through with
 *  a met mark, the rows are still there to be compared against — these were the
 *  gaps, this is what the tick did to them — and the control that undoes it has
 *  not moved either.
 */
/** `hidden` — covered, but still holding its space.
 *
 *  These rows are what the question below them is about, so they belong above
 *  it. Unmounting them once the answer covers them is what made that control
 *  jump up the page as it was being read; `visibility` keeps the box, so the gap
 *  is blank and a little odd and nothing below it moves. Restating them and
 *  striking them through were both tried, and both left two versions of one fact
 *  on screen at once.
 */
export function Shortfall({ blockers = [], realmName, variant = 'prose', hidden = false }) {
  // 'no-realm' never reaches here — the caller gates on a realm being chosen —
  // but a row carrying it would describe the realms as refusing something they
  // were never asked, so it is dropped rather than rendered.
  const real = blockers.filter((b) => b.reason && b.reason !== 'no-realm');
  if (!real.length) return null;
  const name = realmName || 'the realms you added';

  if (variant === 'table') {
    const byType = new Map();
    for (const b of real) {
      const k = typeOf(b.grant);
      if (!byType.has(k)) byType.set(k, { verbs: [], causes: new Set() });
      byType.get(k).verbs.push(b.grant.verb);
      byType.get(k).causes.add(b.reason);
    }
    return (
      <ul className={`gp-short gp-short--table${hidden ? ' is-hidden' : ''}`}
          data-testid="shortfall" aria-hidden={hidden || undefined}>
        {[...byType.entries()].map(([type, { verbs, causes }]) => (
          <li key={type} className="gp-short__trow">
            <span className="gp-short__mark" aria-hidden="true">×</span>
            <span className="gp-short__type">{type}</span>
            <span className="gp-short__verbs">{verbs.join(' ')}</span>
            {/* A type blocked for two reasons at once names neither, for the
                same reason two realms do: no single cause is true of the row. */}
            <span className="gp-short__why">
              {causes.size === 1
                ? SHORT_TABLE_CAUSE[[...causes][0]](name)
                : `not covered by ${name}`}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  const byCause = new Map();
  for (const b of real) {
    if (!byCause.has(b.reason)) byCause.set(b.reason, []);
    byCause.get(b.reason).push(b.grant);
  }
  // Worst-understood first: a withheld verb is the commonest cause and the one
  // a person can act on fastest.
  const order = ['verb', 'schema', 'other', 'mixed'];
  return (
    <ul className={`gp-short${hidden ? ' is-hidden' : ''}`} data-testid="shortfall"
        aria-hidden={hidden || undefined}>
      {order.filter((c) => byCause.has(c)).map((cause) => (
        <li key={cause} className="gp-short__row">
          <span className="gp-short__mark" aria-hidden="true">×</span>
          <span>
            <b>{SHORTFALL_CAUSE[cause](name)}:</b>{' '}
            {/* THE LINE LISTS WHAT IT IS ABOUT. Under "Types not held", naming
                the rows gives "read on tasks, edit on tasks" — the same type
                twice, under a heading about types. The type is the fact there;
                the permission is the fact on every other line. */}
            {cause === 'schema'
              ? andAll([...new Set(byCause.get(cause).map(typeOf))])
              : groupedLabels(byCause.get(cause))}.
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * THE RECEIPT — what this grant actually hands over, itemised.
 *
 * The page states the ASK (what it wants) and the REALMS (what you allow). It
 * never stated the RESULT: the two intersected, in one place you can read back
 * before pressing the button.
 *
 * FOLDED BY DEFAULT, and the total lives on the trigger — so the closed form
 * still answers the headline question and you open it only for the breakdown.
 * Unfolded it competed with Deny/Authorize for the last thing you read, which
 * is the wrong fight for a record to pick.
 *
 * OFFICIAL COMES FROM ALIGNMENT, NOT ORNAMENT: one column of nouns, one of
 * verbs, one of right-aligned tabular figures. No card, no shadow, no accent —
 * a record that decorates itself reads as an advertisement.
 *
 * IT APPEARS ONLY ONCE THERE IS SOMETHING TO ITEMISE. With no realm picked
 * nothing is granted, and a receipt for nothing is a form.
 */
export function Receipt({ rows = [], actions = [], total = 0, realmLabel, expiry }) {
  const [open, setOpen] = useState(false);
  if (!rows.length && !actions.length) return null;

  const trigger = (
    <button type="button" className="gp-rcpt__toggle" data-testid="receipt-toggle"
            aria-expanded={open} onClick={() => setOpen((v) => !v)}>
      <span className="gp-rcpt__caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
      Receipt
      <span className="gp-rcpt__sum">
        {/* NAMED, not bare. Open, this said just "Practice" — a word top-right
            with nothing saying what it was. A receipt names the terms it was
            issued under, so it says so. */}
        {/* ONE COUNT, AND IT IS NODES. "3 actions" named a tier now called
            "Other access", and "3 access" is not English — the two cannot share
            a word because one of them has to count. Rather than keep a second
            vocabulary alive for the sake of a number, the summary states the
            figure that is actually countable and the breakdown lists the rest. */}
        {open ? `under ${realmLabel}`
          : plural(total, 'node')}
      </span>
    </button>
  );

  if (!open) return <section className="gp-rcpt is-shut">{trigger}</section>;

  /* An em-dash where there is no number: an action covers no nodes, and padding
     it with 0 to make the column uniform would state something false. */
  const row = (what, how, n) => (
    <div className="gp-rcpt__row" key={what}>
      <span className="gp-rcpt__what">{what}</span>
      <span className="gp-rcpt__how">{how}</span>
      <span className="gp-rcpt__n">{n == null ? '—' : fmt(n)}</span>
    </div>
  );

  return (
    <section className="gp-rcpt" data-testid="receipt"
             aria-label="What this grant hands over">
      {trigger}
      {rows.length > 0 && (
        <>
          <div className="gp-rcpt__grp">Nodes</div>
          {rows.map((r) => (
            <React.Fragment key={r.name}>
              {row(r.name, r.verbs.join(' · '), r.count)}
              {(r.notes || []).map((t) => <div className="gp-rcpt__note" key={t}>{t}</div>)}
            </React.Fragment>
          ))}
          <div className="gp-rcpt__row is-total">
            <span className="gp-rcpt__what">Total nodes accessible</span>
            <span className="gp-rcpt__how" />
            <span className="gp-rcpt__n">{fmt(total)}</span>
          </div>
        </>
      )}
      {actions.length > 0 && (
        <>
          <div className="gp-rcpt__grp">Other access</div>
          {actions.map((a) => row(a.text, a.tags.join(' · '), null))}
        </>
      )}
      <p className="gp-rcpt__foot">
        <span>Expires</span> <b>{expiry}</b>
        <span className="gp-rcpt__sep">·</span>
        <span>Revocable at any time, effective on the next request</span>
      </p>
    </section>
  );
}

/** C9 — absent unless there is something to say, so its presence carries signal. */
export function Risk({ title, children }) {
  return (
    <div className="gp-risk" role="note" data-testid="c9">
      {title && <b>{title}</b>}
      {children}
    </div>
  );
}

/** C10 — promises, not settings. Only the adjustable row gets a control. */
export function Terms({ rows = [] }) {
  return (
    <dl className="gp-terms" data-testid="c10">
      {rows.map((r) => (
        <div className="gp-terms__row" key={r.label}>
          <dt>{r.label}</dt>
          <dd>{r.value}{r.note && <small>{r.note}</small>}</dd>
        </div>
      ))}
    </dl>
  );
}

/** C11 — two buttons, labelled with the verb that actually happens. A null
 *  `confirmLabel` leaves the one answer (a read-only sheet's Done). */
export function Decide({ confirmLabel, denyLabel = 'Not now', disabled, danger, onConfirm, onDeny, className = '' }) {
  return (
    <div className={`gp-decide${className ? ` ${className}` : ''}`} data-testid="c11">
      <button type="button" className="gp-btn gp-btn--no" onClick={onDeny}>{denyLabel}</button>
      {confirmLabel != null && (
        <button
          type="button"
          className={`gp-btn gp-btn--yes${danger ? ' is-danger' : ''}`}
          disabled={disabled}
          onClick={onConfirm}
        >
          {confirmLabel}
          {/* Forward, once there is somewhere to go. */}
          {!disabled && <span className="gp-btn__arrow" aria-hidden="true">→</span>}
        </button>
      )}
    </div>
  );
}

/**
 * The encryption retry.
 *
 * `ENCRYPTION_KEY_REQUIRED` is a RETRY, not a failure — the backend says so in
 * as many words ("Unlock your encryption key and retry"), rolls the grant back
 * so nothing is half-provisioned, and waits. The old page asked for the
 * password here; the rebuilt one surfaced the sentence as a flat error, which
 * turned a two-second pause into a dead end with instructions the page itself
 * would not let you follow.
 *
 * The password never leaves the browser: it derives the wrapping key that
 * unwraps your private key in memory. Nothing is sent.
 */
export function Unlock({ appName, onUnlock, onCancel, error, busy }) {
  const [password, setPassword] = useState('');
  return (
    <form
      className="gp-unlock" data-testid="unlock"
      onSubmit={(e) => { e.preventDefault(); onUnlock(password); }}
    >
      <b className="gp-unlock__t">Unlock your encryption key</b>
      <p className="gp-unlock__s">
        {appName} reads encrypted fields, so it needs a decryption key of its own.
        Only you can make one. Your password stays in this browser — it unwraps
        your key here and is never sent.
      </p>
      <input
        className="gp-unlock__in" type="password" value={password} autoFocus
        autoComplete="current-password" placeholder="Your password"
        aria-label="Your password"
        onChange={(e) => setPassword(e.target.value)}
      />
      {error && <p className="gp-unlock__err" role="alert">{error}</p>}
      <div className="gp-unlock__row">
        <button type="button" className="gp-btn gp-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="gp-btn gp-btn--go" disabled={!password || busy}>
          {busy ? 'Unlocking…' : 'Unlock and grant'}
        </button>
      </div>
    </form>
  );
}

/** C9b — THE ONE QUESTION THIS STATE RAISES.
 *
 *  A row the chosen realms cannot reach used to be dropped from consent on submit:
 *  the request succeeded, the button said Authorize, and the app came away holding
 *  a grant that could not do its job, with nothing on screen saying so.
 *
 *  IT WAS THREE OPTIONS AND IT IS ONE QUESTION. Offering "add to this realm", "a
 *  new shared realm", and "ignore them" side by side made a person compare three
 *  destinations before they had decided the only thing actually in front of them:
 *  whether the realm they just picked should cover this too. The other answer —
 *  a realm for this app alone — belongs to the state where NO realm is picked,
 *  and it lives under the chooser there, where the question is which realm at all.
 *
 *  So: a realm is picked, and this asks whether to widen it. Unchecked is the
 *  default, because the realm is a shared object and growing it grows every grant
 *  attached to it; unchecked also means the app is authorized without these
 *  permissions, which is what the line says.
 */
/** C9b — A DIVIDER, AND BEHIND IT THE TWO PLACES THE LEFTOVERS CAN GO.
 *
 *  SHUT BY DEFAULT, BECAUSE SHUT IS AN ANSWER. Doing nothing about the rows
 *  above is a real outcome — the app is authorized without them, which the foot
 *  of the page states as "Authorize 1 of 4" — and it is the common one. A
 *  divider is the whole offer in one grey line: it says something can be done
 *  here without spending two lines saying what, and it keeps the page's last
 *  word the button rather than a choice nobody asked for.
 *
 *  OPTIONS, NOT CHECKBOXES. The two destinations are exclusive on the wire
 *  (widen_realm_id XOR mint_shared_realm), so the shape says so before anyone
 *  finds out by clicking. What a checkbox would have given up is the third
 *  answer — neither — so both ways back to it are here: clicking the chosen one
 *  again clears it, and closing the divider clears it on the way out. Without
 *  the second there would be a state where an option reads as chosen behind a
 *  shut line while the wire carries it.
 */
export function Placement({ appName, count = 0, value = null, pairRealmId = null,
                            target = null, choices = [], onChange, onPickRealm,
                            owner = 'your', actorName = null, unbounded = false,
                            unboundedVerbs = [], impact = null }) {
  const [open, setOpen] = useState(false);
  if (!count) return null;
  const perms = count === 1 ? 'permission' : 'permissions';
  const shared = value === 'shared';
  const widen = !!target && String(value) === String(target.id);
  const toggle = () => {
    // Closing is how you decline, so it cannot leave an answer behind.
    if (open) onChange?.('none');
    setOpen(!open);
  };
  const opt = (v, on, body) => (
    <label className={`gp-place__ask${on ? ' is-on' : ''}`}>
      {/* A RADIO YOU CAN CLICK OFF. Nothing in HTML unselects a radio, so the
          choice would otherwise be one-way once made: picking is reversible only
          into the other option, never into neither. onClick fires before the
          prop updates, so on an unchecked one `on` is still false and this does
          nothing; onChange then sets it. On a checked one onChange does not fire
          at all, and this is the only thing that runs. */}
      <input type="radio" className="gp-place__pick" name="gp-placement" checked={on}
             onChange={() => onChange?.(v)}
             onClick={() => { if (on) onChange?.('none'); }} />
      <span>{body}</span>
    </label>
  );
  return (
    <div className="gp-place" data-testid="placement">
      <button type="button" className={`gp-place__div${open ? ' is-open' : ''}`}
              aria-expanded={open} data-testid="placement-divider" onClick={toggle}>
        <span className="gp-place__div-label">
          <span className="gp-place__caret" aria-hidden="true" />
          Handle the remaining {perms}
        </span>
        <span className="gp-place__rule" aria-hidden="true" />
      </button>

      {open && (
        <div className="gp-place__body">
          {/* THE ONE WITH NO CONSEQUENCE LEADS. A realm between you and this app
              alone touches nothing else; widening one you already hold reaches
              every other grant attached to it, which is why that one carries a
              warning — and a warning is not what the first thing read should be. */}
          {/* ON A GROUP EDGE IT IS THE GROUP'S, NOT YOURS. The realm belongs to
              the group and the pair is the group and the app; saying "your" and
              "you" here told an admin they were handing over their own graph.
              `owner` is the page's own word for this, the one C3 and C7 use. */}
          {opt('shared', shared, (
            pairRealmId
              ? <>Add the remaining {perms} to {owner} shared realm with <b>{appName}</b></>
              : (<>Add the remaining {perms} to a shared realm between{' '}
                  {actorName ? <b>{actorName}</b> : 'you'} and <b>{appName}</b></>)
          ))}
          {/* ONE DESTINATION, WHEN THE OTHER WOULD BE A LIE. An ask with no type
              limit is `{}` — TOP — and adding it to a realm you already use makes
              that realm unrestricted for everything on it. The server refuses
              (WIDEN_UNBOUNDED); offering it here and letting the grant fail at the
              button is the worse half of that. The app's own realm still takes it,
              where the only grant affected is the one asking.

              AND THE ABSENCE IS NOT EXPLAINED. A note here said the app "asks for
              whole permissions rather than named types" — the rule as the engine
              states it, in front of someone who only wanted to know where their
              data goes. The rows above already say it in the page's own words
              ("Read all of your data", "read on everything"), and an option that
              is not offered does not owe anyone a reason. */}
          {target && opt(target.id, widen, (
            <>
              Add the remaining {perms} to {owner} existing realm,{' '}
              {choices.length === 1
                ? <b>{target.name}</b>
                : (<select className="gp-place__which" value={String(target.id)}
                           onClick={(e) => e.stopPropagation()}
                           onChange={(e) => onPickRealm?.(e.target.value)}>
                    {choices.map((r) => (
                      <option key={r.id} value={String(r.id)}>{r.name}</option>
                    ))}
                  </select>)}
            </>
          ))}

          {/* UNDER BOTH, NOT BETWEEN THEM. Set between them it pushed the second
              option down the moment the first was chosen — the list you are
              reading rearranging itself as you read it. It names its realm, so
              nothing says which option it belongs to and nothing needs to.

              ONLY THE WIDENING SAYS ANYTHING. The other option had a line too —
              that the realms you picked are unchanged — and it was reassurance
              about a thing that never happens, under an option whose own words
              already say the realm is between you and this app alone. A footnote
              that appears whatever you choose stops being read, and takes the
              warning with it.

              A realm is a shared object: effective access is consent ∩ ceiling
              and `{}` means TOP, so every grant attached to the realm being
              widened that is unrestricted on these verbs takes the wider ceiling
              verbatim, with no re-authorization anywhere. */}
          {widen && (
            <p className="gp-place__warn is-on" data-testid="placement-warning">
              <span className="gp-place__warn-mark">Note:</span> this widens{' '}
              <b>{target.name}</b>, which has sharing consequences if you are
              using it to share to other apps
              {/* AND WHO, BY NAME, RATHER THAN "CONSEQUENCES".
                  A sentence true of any widening is one a reader skips. The
                  grants that gain are fetched the moment this option is chosen,
                  so by the time anyone has read this far there is usually a list
                  — and when there is not, saying nothing is honest: nobody else
                  is attached, and inventing a caution for that is how the real
                  one stops being read. */}
              {impact?.gaining?.length ? (
                <> — <b>{listNames(impact.gaining.map(nameOfGainer))}</b>{' '}
                  {impact.gaining.length === 1 ? 'gains' : 'gain'} it too
                </>
              ) : null}
            </p>
          )}

          {/* THE ONE THAT IS NOT A WIDENING BUT A REMOVAL OF THE LIMIT.
              `{}` is TOP: the realm stops naming types and starts meaning every
              type, for everything attached to it now and everything attached
              later. The impact list above cannot say the second half — it can
              only count today's grants — so the categorical sentence is said
              here, in its own line, and not folded into a clause.

              It is shown INSTEAD OF nothing, never instead of the choice. This
              was refused outright until the page could name the cost; the answer
              to a grave consequence is to state it, not to decide for someone
              that they may not have it. */}
          {widen && unbounded && (
            <p className="gp-place__warn gp-place__warn--top is-on"
               data-testid="placement-unbounded">
              <span className="gp-place__warn-mark">This removes the limit:</span>{' '}
              <b>{target.name}</b> stops naming types for{' '}
              {listNames(unboundedVerbs.map(verbPhrase))} and covers everything —
              including anything attached to it later.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** C4b — THE ANSWER WHEN NONE OF YOUR REALMS IS ABOUT THIS APP.
 *
 *  Under the chooser, because that is the question it answers: not "where do these
 *  permissions go" but "which realm at all". A purpose-built realm has no reason to
 *  contain a new app's schemas, so for a first authorization the honest answer is
 *  often none of them — and the only way to say that used to be attaching one wider
 *  than the ask, which is how "the minimum" became Full Access.
 *
 *  The realm it offers holds exactly what the app asked for and nothing else, and
 *  it is keyed by the pair: the same realm is reused and widened by every later
 *  grant to this app rather than a new one each time.
 */
export function SharedRealmOffer({ appName, exists = false, on = false, onToggle,
                                   owner = 'your', actorName = null }) {
  return (
    <label className={`gp-offer${on ? ' is-on' : ''}`} data-testid="shared-realm-offer">
      <input type="checkbox" className="gp-place__pick" checked={on}
             onChange={() => onToggle?.(!on)} />
      <span>
        <b>Don’t want any of the above?</b>{' '}
        {/* ON A GROUP EDGE THE PAIR IS THE GROUP AND THE APP, same as the
            placement control below the rows. This is the first thing an admin
            reads about a realm being made for them, and it was naming them
            rather than the group whose graph is in play. */}
        {exists
          ? <>Use {owner} shared realm with <b>{appName}</b> — it holds only what {appName} asked for.</>
          : (<>Create a shared realm between {actorName ? <b>{actorName}</b> : 'you'} and{' '}
              <b>{appName}</b> — it will hold only what {appName} asked for.</>)}
      </span>
    </label>
  );
}

/** A gaining grant's name as a reader knows it: the app, and the agent under it
 *  when the grant belongs to one. `entity_name` is already resolved server-side;
 *  the key is here so a nameless row degrades to its type rather than to blank. */
function nameOfGainer(g = {}) {
  return [g.entity_name || g.entity_type, g.sub_entity_key].filter(Boolean).join(' / ');
}

/** `nodes:read` as the page says it everywhere else. The raw key is the engine's
 *  word, and this line is the one place a person is asked to weigh it. */
function verbPhrase(key = '') {
  const [resource, verb] = String(key).split(':');
  return VERB_LABELS[`${resource}.${verb}`] || String(key);
}

/** "Priya", "Priya and Ledger", "Priya, Ledger and 2 others" — a list someone can
 *  read at the width this line has. */
function listNames(names = []) {
  if (names.length <= 2) return names.join(' and ');
  const rest = names.length - 2;
  return `${names.slice(0, 2).join(', ')} and ${plural(rest, 'other')}`;
}
